import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Image,
  TextInput,
  Alert,
} from 'react-native';
import LoadingAnimation from '../LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useAuth } from '../../services/AuthContext';
import { getUserByClerkId, getUserById } from '../../services/database';
import { getOrCreateConversation, subscribeToMessages as subscribeToMessagesSupabase, getUserConversations, formatConversation, deleteConversation } from '../../services/chatService';
import { supabase } from '../../services/supabase';
import { getProfilePictureUrl } from '../../services/storage';
import { useFocusEffect } from '@react-navigation/native';
import { getColors, typography, spacing, borderRadius, getShadows } from '../../styles/theme';
import { useChatCache } from '../../contexts/ChatCacheContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';



// Static styles for ProfileImage (outside component)
const profileImageStyles = StyleSheet.create({
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  avatarPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

// Profile Image Component that handles S3 URLs with caching
const ProfileImage = ({ profilePictureUrl, accountType, size = 'thumbnail' }) => {
  const { isDarkMode } = useTheme();
  const themeColors = getColors(isDarkMode);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      if (!profilePictureUrl) {
        setLoading(false);
        setImageUrl(null);
        return;
      }

      try {
        // Use Supabase storage helper (handles caching internally)
        const url = getProfilePictureUrl(profilePictureUrl, size);
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading profile image:', error);
        setImageUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [profilePictureUrl, size]);

  const handleImageError = () => {
    // If we haven't tried the fallback yet and we're using a sized version
    if (!triedFallback && size !== 'original') {
      setTriedFallback(true);
      setLoading(true);
      
      try {
        // Try loading the original image instead
        const fallbackUrl = getProfilePictureUrl(profilePictureUrl, 'original');
        setImageUrl(fallbackUrl);
        setLoading(false);
      } catch (error) {
        // Give up silently
        setImageUrl(null);
        setLoading(false);
      }
    } else {
      // Give up and show placeholder
      setImageUrl(null);
    }
  };

  if (loading) {
    return (
      <View style={[profileImageStyles.avatar, profileImageStyles.avatarPlaceholder, { backgroundColor: themeColors.backgroundDark }]}>
        <Icon name="person" size={28} color={themeColors.textLight} />
      </View>
    );
  }

  if (!imageUrl || !profilePictureUrl) {
    return (
      <View style={[profileImageStyles.avatar, profileImageStyles.avatarPlaceholder, { backgroundColor: themeColors.backgroundDark }]}>
        <Icon 
          name={accountType === 'CLUB' ? 'business' : 'person'} 
          size={28} 
          color={themeColors.textLight}
        />
      </View>
    );
  }

  return (
    <Image 
      source={{ uri: imageUrl }} 
      style={profileImageStyles.avatar}
      resizeMode="cover"
      onError={handleImageError}
    />
  );
};

const ChatListPanel = ({ navigation, onSelectConversation, selectedConversationId, isMobile, openUserId, openUser }) => {
  const { setActiveConversationId } = useChatNotification();

  // Track the selected conversation so the notification context can suppress toasts
  useEffect(() => {
    setActiveConversationId(selectedConversationId || null);
    return () => setActiveConversationId(null);
  }, [selectedConversationId, setActiveConversationId]);

  // Helper to open conversation - always use callback (ChatContainer handles mobile/desktop)
  const openConversation = (conversation) => {
    if (!conversation || !conversation.id) {
      console.error('❌ Invalid conversation object:', conversation);
      return;
    }

    if (!conversation.otherUser || !conversation.otherUser.id) {
      console.error('❌ Conversation missing otherUser:', conversation);
      return;
    }

    // Always use the callback - ChatContainer will handle mobile vs desktop display
    console.log('✅ Opening conversation:', conversation.id);
    onSelectConversation(conversation);
  };
  const { user, appUser, clerkUserId } = useAuth();
  const { cacheConversation } = useChatCache();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' or 'unread'
  const [searchQuery, setSearchQuery] = useState('');
  const subscriptionRef = useRef(null);
  const hasOpenedConversationRef = useRef(false);

  useFocusEffect(
    React.useCallback(() => {
      const initializeChat = async () => {
        try {
          const dbId = await fetchCurrentUserDbId();
          if (dbId) {
            await fetchConversations(dbId);
            // Subscribe after we have the user DB ID
            subscribeToNewMessages();
          } else {
            // If we can't get the user DB ID, stop loading
            setLoading(false);
          }
        } catch (error) {
          console.error('Error initializing chat:', error);
          setLoading(false);
        }
      };
      
      initializeChat();
      
      return () => {
        if (subscriptionRef.current) {
          console.log('🧹 Cleaning up subscription');
          try {
            // If it's a Supabase unsubscribe function, call it
            if (typeof subscriptionRef.current === 'function') {
              subscriptionRef.current();
            } else if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
              // Handle AppSync subscriptions (legacy)
              subscriptionRef.current.unsubscribe();
            }
          } catch (error) {
            console.error('Error cleaning up subscription:', error);
          }
          subscriptionRef.current = null;
        }
      };
    }, [currentUserDbId])
  );

  // Update currentUserDbId when appUser changes
  useEffect(() => {
    if (appUser?.id && appUser.id !== currentUserDbId) {
      console.log('✅ Updating currentUserDbId from appUser:', appUser.id);
      setCurrentUserDbId(appUser.id);
    }
  }, [appUser?.id]);

  // Fetch conversations when currentUserDbId becomes available
  useEffect(() => {
    if (currentUserDbId) {
      fetchConversations();
      subscribeToNewMessages();
    }
  }, [currentUserDbId]);

  // Update navigation badge when unread count changes (mobile only)
  useEffect(() => {
    if (isMobile && navigation.setOptions) {
      navigation.setOptions({
        tabBarBadge: totalUnreadCount > 0 ? totalUnreadCount : null,
      });
    }
  }, [totalUnreadCount, navigation, isMobile]);

  // Apply filtering and search
  useEffect(() => {
    let filtered = conversations;

    // Apply unread filter
    if (activeFilter === 'unread') {
      filtered = filtered.filter(conv => conv.unreadCount > 0);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => {
        const username = conv.otherUser?.username?.toLowerCase() || '';
        const clubName = conv.otherUser?.clubName?.toLowerCase() || '';
        return username.includes(query) || clubName.includes(query);
      });
    }

    setFilteredConversations(filtered);
  }, [conversations, activeFilter, searchQuery]);

  const fetchCurrentUserDbId = async () => {
    try {
      // Use appUser.id directly if available (Supabase database ID)
      if (appUser?.id) {
        console.log('✅ Using appUser.id:', appUser.id);
        setCurrentUserDbId(appUser.id);
        return appUser.id;
      }

      // Fallback: fetch by Clerk ID if appUser not loaded yet
      if (clerkUserId) {
        console.log('🔄 Fetching user by Clerk ID:', clerkUserId);
        const dbUser = await getUserByClerkId(clerkUserId);
        if (dbUser?.id) {
          console.log('✅ Fetched user DB ID:', dbUser.id);
          setCurrentUserDbId(dbUser.id);
          return dbUser.id;
        }
      }

      console.warn('⚠️ No user DB ID available');
      return null;
    } catch (error) {
      console.error('Error fetching current user DB ID:', error);
      return null;
    }
  };

  const fetchConversations = async (dbIdOverride = null, isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      // Use the provided dbId or fall back to state
      const userDbId = dbIdOverride || currentUserDbId;

      if (!userDbId) {
        console.log('⏳ Waiting for currentUserDbId before fetching conversations...');
        setLoading(false);
        return;
      }

      console.log('📥 Fetching conversations for user:', userDbId);

      // Use Supabase to get conversations
      const supabaseConversations = await getUserConversations(userDbId);
      console.log('📨 Fetched conversations from Supabase:', supabaseConversations.length);

      // Format conversations and get unread counts
      const conversationsWithUserData = await Promise.all(
        supabaseConversations.map(async (conv) => {
          // Format the conversation (converts snake_case to camelCase)
          const formatted = formatConversation(conv, userDbId);
          
          if (!formatted.otherUser) {
            console.warn('⚠️ Conversation missing otherUser:', conv.id);
            return null;
          }

          // Get unread count for this conversation
          const { count, error: unreadError } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('conversation_id', conv.id)
            .eq('receiver_id', userDbId)
            .eq('read', false);

          if (unreadError) {
            console.error('Error fetching unread count:', unreadError);
          }

          const unreadCount = count || 0;

          // Get latest messages for caching
          const { data: messages } = await supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conv.id)
            .order('created_at', { ascending: false })
            .limit(50);

          // Cache this conversation
          if (messages && messages.length > 0) {
            const formattedMessages = messages.reverse().map(msg => ({
              id: msg.id,
              conversationId: msg.conversation_id,
              senderId: msg.sender_id,
              receiverId: msg.receiver_id,
              content: msg.content,
              createdAt: msg.created_at,
              read: msg.read,
            }));

            cacheConversation(conv.id, {
              messages: formattedMessages,
              otherUser: formatted.otherUser,
            });
          }

          return {
            id: formatted.id,
            lastMessageAt: formatted.lastMessageAt,
            otherUser: formatted.otherUser,
            unreadCount,
            latestMessage: formatted.latestMessage,
          };
        })
      );

      // Filter out null conversations and sort
      const validConversations = conversationsWithUserData.filter(Boolean);
      const sorted = validConversations.sort(
        (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
      );

      const totalUnread = sorted.reduce((sum, conv) => sum + conv.unreadCount, 0);
      
      console.log('✅ Loaded conversations:', {
        count: sorted.length,
        totalUnread,
      });

      setConversations(sorted);
      setTotalUnreadCount(totalUnread);
    } catch (error) {
      console.error('❌ Error fetching conversations:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset the opened conversation ref when openUserId changes
  useEffect(() => {
    hasOpenedConversationRef.current = false;
  }, [openUserId]);

  // Handle opening a conversation when openUserId is provided
  useEffect(() => {
    // Wait for conversations to finish loading before trying to open/create
    if (!openUserId || !currentUserDbId || loading || hasOpenedConversationRef.current) {
      return;
    }

    // Small delay to ensure conversations state is stable
    const timeoutId = setTimeout(() => {
      const openConversationWithUser = async () => {
        try {
          console.log('🔍 Looking for conversation with user:', openUserId);
          console.log('📋 Current conversations count:', conversations.length);
          console.log('👤 Current user DB ID:', currentUserDbId);
          
          // First, check if a conversation already exists with this user
          const existingConversation = conversations.find(conv => 
            conv.otherUser?.id === openUserId
          );

          if (existingConversation) {
            console.log('✅ Found existing conversation, opening it:', existingConversation.id);
            hasOpenedConversationRef.current = true;
            // Use a small delay to ensure UI is ready
            setTimeout(() => {
              openConversation(existingConversation);
            }, 100);
            return;
          }

          // If no conversation exists, create a DRAFT conversation (not saved to DB yet)
          // Conversation will only be created when first message is sent
          console.log('📝 No existing conversation found, opening draft chat with user:', openUserId);
          
          // Fetch user data if not provided
          let userData = openUser;
          if (!userData) {
            try {
              // Use Supabase to fetch user data
              const dbUser = await getUserById(openUserId);
              if (dbUser) {
                // Convert snake_case to camelCase
                userData = {
                  id: dbUser.id,
                  username: dbUser.username,
                  clubName: dbUser.club_name,
                  accountType: dbUser.account_type,
                  profilePictureUrl: dbUser.profile_picture_url,
                };
                console.log('✅ Fetched user data from Supabase:', userData);
              } else {
                throw new Error('User not found');
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
              userData = {
                id: openUserId,
                username: 'User',
                accountType: 'PLAYER',
              };
            }
          }
          
          // Create a DRAFT conversation object (no ID yet - will be created when first message is sent)
          // Use a temporary ID that starts with "draft-" to identify it
          const draftConversationId = `draft-${currentUserDbId}-${openUserId}`;
          const draftConversation = {
            id: draftConversationId,
            lastMessageAt: null, // No messages yet
            messages: { items: [] },
            otherUser: userData,
            unreadCount: 0,
            isDraft: true, // Mark as draft
          };

          // Mark as opened immediately to prevent re-triggering
          hasOpenedConversationRef.current = true;
          
          // DON'T add draft conversations to the conversations list
          // They will only appear once a message is sent and conversation is created
          
          // Open the draft conversation immediately
          setTimeout(() => {
            console.log('✅ Opening draft conversation (will create on first message)');
            openConversation(draftConversation);
          }, 100);
        } catch (error) {
          console.error('❌ Error opening conversation with user:', error);
          console.error('Error details:', error.message, error.stack);
        }
      };

      openConversationWithUser();
    }, 300); // Wait 300ms for conversations to stabilize

    return () => clearTimeout(timeoutId);
  }, [openUserId, currentUserDbId, conversations, loading, onSelectConversation, openUser]);

  const subscribeToNewMessages = () => {
    // Only subscribe if we have the current user DB ID
    if (!currentUserDbId) {
      console.log('⏳ Waiting for currentUserDbId before subscribing...');
      return;
    }

    // Clean up existing subscription if any
    if (subscriptionRef.current) {
      console.log('🧹 Cleaning up existing subscription');
      // If it's a Supabase unsubscribe function, call it
      if (typeof subscriptionRef.current === 'function') {
        subscriptionRef.current();
      } else if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
        // Handle AppSync subscriptions (legacy)
        subscriptionRef.current.unsubscribe();
      }
      subscriptionRef.current = null;
    }

    try {
      // Use Supabase real-time subscription for new messages
      const unsubscribeMessages = subscribeToMessagesSupabase(currentUserDbId, async (newMessage) => {
        console.log('🔔 New message received via Supabase subscription');
        
        // Background refresh (no loading spinner)
        fetchConversations(currentUserDbId, true);
      });

      // Subscribe to read status updates to refresh unread counts immediately
      const readStatusChannel = supabase
        .channel(`read-status-updates-${currentUserDbId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${currentUserDbId}`,
          },
          (payload) => {
            // Only refresh if the read status actually changed
            if (payload.new.read !== payload.old.read && payload.new.read === true) {
              console.log('📖 Message marked as read, refreshing unread counts');
              // Background refresh (no loading spinner)
              fetchConversations(currentUserDbId, true);
            }
          }
        )
        .subscribe();

      // Return a cleanup function that unsubscribes from both
      subscriptionRef.current = () => {
        unsubscribeMessages();
        supabase.removeChannel(readStatusChannel);
      };
      
      console.log('✅ Successfully subscribed to new messages and read status updates via Supabase');
    } catch (error) {
      console.error('❌ Error setting up Supabase subscription:', error);
      subscriptionRef.current = null;
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    Alert.alert(
      'Delete Conversation',
      'Are you sure you want to delete this conversation? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Use Supabase to delete (CASCADE will remove messages and conversation_users)
              await deleteConversation(conversationId);
              // Refresh the list
              fetchConversations();
            } catch (error) {
              console.error('Error deleting conversation:', error);
              Alert.alert('Error', 'Failed to delete conversation');
            }
          },
        },
      ]
    );
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const renderConversation = ({ item }) => {
    const lastMessage = item.latestMessage;
    const displayName = item.otherUser?.clubName || item.otherUser?.username || 'Unknown';
    const isSelected = selectedConversationId === item.id;
    const hasMessages = lastMessage != null && lastMessage.content != null;

    return (
      <View style={styles.conversationItemWrapper}>
        <TouchableOpacity
          style={[
            styles.conversationItem,
            isSelected && styles.conversationItemSelected,
          ]}
          onPress={() => onSelectConversation(item)}
        >
          <View style={styles.avatarContainer}>
            <ProfileImage 
              profilePictureUrl={item.otherUser?.profilePictureUrl}
              accountType={item.otherUser?.accountType}
            />
            {item.unreadCount > 0 && (
              <View style={styles.unreadDot} />
            )}
          </View>

          <View style={styles.conversationInfo}>
            <View style={styles.conversationHeader}>
              <Text style={styles.conversationName} numberOfLines={1}>
                {displayName}
              </Text>
              {hasMessages && lastMessage.createdAt && (
                <Text style={styles.conversationTime}>
                  {formatMessageTime(lastMessage.createdAt)}
                </Text>
              )}
            </View>
            <View style={styles.conversationPreview}>
              {hasMessages ? (
                <>
                  <Text style={styles.conversationMessage} numberOfLines={1}>
                    {lastMessage.content}
                  </Text>
                  {item.unreadCount > 0 && (
                    <View style={styles.unreadBadge}>
                      <Text style={styles.unreadBadgeText}>{item.unreadCount}</Text>
                    </View>
                  )}
                </>
              ) : (
                <Text style={[styles.conversationMessage, { fontStyle: 'italic', color: colors.textSecondary }]} numberOfLines={1}>
                  Start conversation
                </Text>
              )}
            </View>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => handleDeleteConversation(item.id)}
        >
          <Icon name="delete" size={20} color={colors.error} />
        </TouchableOpacity>
      </View>
    );
  };

  const styles = getStyles(colors, shadows);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Chats</Text>
        {isMobile && (
          <TouchableOpacity>
            <Icon name="more-vert" size={24} color={colors.text} />
          </TouchableOpacity>
        )}
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Icon name="search" size={20} color={colors.textSecondary} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search Messenger"
          placeholderTextColor={colors.textSecondary}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      {/* Filter Tabs */}
      <View style={styles.filterContainer}>
        <TouchableOpacity
          style={[
            styles.filterTab,
            activeFilter === 'all' && styles.filterTabActive,
          ]}
          onPress={() => setActiveFilter('all')}
        >
          <Text
            style={[
              styles.filterTabText,
              activeFilter === 'all' && styles.filterTabTextActive,
            ]}
          >
            All
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.filterTab,
            activeFilter === 'unread' && styles.filterTabActive,
          ]}
          onPress={() => setActiveFilter('unread')}
        >
          <Text
            style={[
              styles.filterTabText,
              activeFilter === 'unread' && styles.filterTabTextActive,
            ]}
          >
            Unread
          </Text>
          {totalUnreadCount > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{totalUnreadCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Conversations List */}
      <FlatList
        data={filteredConversations}
        keyExtractor={(item) => item.id}
        renderItem={renderConversation}
        contentContainerStyle={styles.list}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Icon name="chat-bubble-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyStateText}>
              {activeFilter === 'unread' ? 'No unread messages' : 'Start Chatting'}
            </Text>
            {activeFilter === 'all' && (
              <Text style={styles.emptyStateSubtext}>
                Chats will be here
              </Text>
            )}
          </View>
        }
      />
    </View>
  );
};

// Dynamic styles based on theme
const getStyles = (colors, shadows) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surface,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  headerTitle: {
    fontSize: typography.h4,
    fontWeight: typography.bold,
    color: colors.text,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.backgroundDark,
    borderRadius: borderRadius.pill,
  },
  searchInput: {
    flex: 1,
    marginLeft: spacing.sm,
    fontSize: typography.body,
    color: colors.text,
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  filterTab: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginRight: spacing.sm,
    borderRadius: borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
  },
  filterTabActive: {
    backgroundColor: colors.primary + '20',
  },
  filterTabText: {
    fontSize: typography.body,
    fontWeight: typography.medium,
    color: colors.textSecondary,
  },
  filterTabTextActive: {
    color: colors.primary,
    fontWeight: typography.semiBold,
  },
  filterBadge: {
    marginLeft: spacing.xs,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.pill,
    paddingHorizontal: spacing.xs + 2,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  filterBadgeText: {
    fontSize: typography.caption,
    fontWeight: typography.bold,
    color: colors.textInverse,
  },
  list: {
    paddingBottom: spacing.md,
  },
  conversationItemWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  conversationItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingRight: spacing.sm,
    borderRadius: borderRadius.md,
  },
  conversationItemSelected: {
    backgroundColor: colors.backgroundDark,
  },
  avatarContainer: {
    position: 'relative',
    marginRight: spacing.md,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  avatarPlaceholder: {
    backgroundColor: colors.backgroundDark,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: colors.primary,
    borderWidth: 2,
    borderColor: colors.surface,
  },
  conversationInfo: {
    flex: 1,
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  conversationName: {
    flex: 1,
    fontSize: typography.body,
    fontWeight: typography.semiBold,
    color: colors.text,
  },
  conversationTime: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    marginLeft: spacing.sm,
  },
  conversationPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  conversationMessage: {
    flex: 1,
    fontSize: typography.bodySmall,
    color: colors.textSecondary,
  },
  unreadBadge: {
    backgroundColor: colors.primary,
    borderRadius: borderRadius.pill,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
    marginLeft: spacing.sm,
  },
  unreadBadgeText: {
    fontSize: typography.caption,
    fontWeight: typography.bold,
    color: colors.textInverse,
  },
  deleteButton: {
    padding: spacing.sm,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyStateText: {
    marginTop: spacing.md,
    fontSize: typography.body,
    color: colors.text,
    textAlign: 'center',
    fontWeight: typography.semiBold,
  },
  emptyStateSubtext: {
    marginTop: spacing.sm,
    fontSize: typography.bodySmall,
    color: colors.textSecondary,
    textAlign: 'center',
  },
});

export default ChatListPanel;

